import type { ComponentType } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { LoadingBlock } from '@/design'
import { BusinessOverviewPage } from '@/features/analytics/BusinessOverviewPage'
import { AbandonmentPage } from '@/features/analytics/abandonment/AbandonmentPage'
import { AdvertisingPage } from '@/features/analytics/advertising/AdvertisingPage'
import { CompetitionDetailPage } from '@/features/analytics/competitionDetail/CompetitionDetailPage'
import { CompetitionsPage } from '@/features/analytics/competitions/CompetitionsPage'
import { CustomersPage } from '@/features/analytics/customers/CustomersPage'
import { FunnelPage } from '@/features/analytics/funnel/FunnelPage'
import { RevenuePage } from '@/features/analytics/revenue/RevenuePage'
import { TrafficPage } from '@/features/analytics/traffic/TrafficPage'
import { ActivityPage as AutomationActivityPage } from '@/features/automation/ActivityPage'
import { ScheduledPage as AutomationScheduledPage } from '@/features/automation/ScheduledPage'
import { TriggersPage } from '@/features/automation/TriggersPage'
import { WorkflowDetailPage } from '@/features/automation/WorkflowDetailPage'
import { WorkflowsPage } from '@/features/automation/WorkflowsPage'
import { AdvertisingOverviewPage } from '@/features/marketing/advertising/AdvertisingOverviewPage'
import { AttributionPage } from '@/features/marketing/attribution/AttributionPage'
import { CampaignDetailPage } from '@/features/marketing/campaigns/CampaignDetailPage'
import { CampaignsListPage } from '@/features/marketing/campaigns/CampaignsListPage'
import { CampaignWizardPage } from '@/features/marketing/campaigns/wizard/CampaignWizardPage'
import { ActivityPage } from '@/features/market/ActivityPage'
import { CompetitorDetailPage } from '@/features/market/CompetitorDetailPage'
import { CompetitorsPage } from '@/features/market/CompetitorsPage'
import { PricingPage } from '@/features/market/PricingPage'
import { PrizeTrendsPage } from '@/features/market/PrizeTrendsPage'
import { TrackedCompetitionsPage } from '@/features/market/TrackedCompetitionsPage'
import { ListingFormPage } from '@/features/listings/ListingFormPage'
import { ListingsPage } from '@/features/listings/ListingsPage'
import { PublishingPage } from '@/features/listings/PublishingPage'
import { TemplatesPage } from '@/features/listings/TemplatesPage'
import { AiDigestPage } from '@/features/insights/AiDigestPage'
import { AlertsPage } from '@/features/insights/AlertsPage'
import { RecommendationsPage } from '@/features/insights/RecommendationsPage'
import { TrendsPage } from '@/features/insights/TrendsPage'
import { OverviewPage } from '@/features/overview/OverviewPage'
import { ExportsPage } from '@/features/reports/ExportsPage'
import { ReportDetailPage } from '@/features/reports/ReportDetailPage'
import { ReportsListPage } from '@/features/reports/ReportsListPage'
import { AuditLogPage } from '@/features/settings/audit'
import { BrandingSettingsPage } from '@/features/settings/branding/BrandingSettingsPage'
import { DomainsPage } from '@/features/settings/domains'
import { IntegrationProviderPage, IntegrationsPage } from '@/features/integrations'
import { UsersPage } from '@/features/settings/users'
import { TrackingSettingsPage } from '@/features/settings/tracking'
import { WebsitesPage } from '@/features/settings/websites'
import { usePermissions } from '@/lib/access'

import { AppShell } from './AppShell'
import { SignedOutGate } from './signedOut'
import { NotFoundPage, PlaceholderPage, RedirectPage, RouteGate, SectionFrame, useSectionTabs } from './pages'
import { ROUTES, SECTION_ALIASES, SECTIONS, type RouteDef, type SectionDef } from './routes'

type RoutedPage = ComponentType<{ route: RouteDef }>

function framed(Page: ComponentType): RoutedPage {
  return function FramedPage({ route }) {
    return (
      <SectionFrame route={route}>
        <Page />
      </SectionFrame>
    )
  }
}

/**
 * The pages that exist. Anything in the route table without an entry here
 * renders `PlaceholderPage`; a feature replaces its placeholder by adding one
 * line to this map.
 */
const PAGES: Partial<Record<string, RoutedPage>> = {
  '/': OverviewPage,
  '/analytics': BusinessOverviewPage,
  '/analytics/website': TrafficPage,
  '/analytics/advertising': AdvertisingPage,
  '/analytics/funnel': FunnelPage,
  '/analytics/abandonment': AbandonmentPage,
  '/analytics/competitions': CompetitionsPage,
  '/analytics/competitions/:id': CompetitionDetailPage,
  '/analytics/customers': CustomersPage,
  '/analytics/revenue': RevenuePage,
  '/insights': framed(AiDigestPage),
  '/insights/digest': framed(AiDigestPage),
  '/insights/recommendations': framed(RecommendationsPage),
  '/insights/alerts': framed(AlertsPage),
  '/insights/trends': framed(TrendsPage),
  '/settings/branding': framed(BrandingSettingsPage),
  '/settings/users': framed(UsersPage),
  '/settings/audit': framed(AuditLogPage),
  '/settings/domains': framed(DomainsPage),
  '/settings/websites': framed(WebsitesPage),
  '/settings/tracking': framed(TrackingSettingsPage),
  '/market/competitors': CompetitorsPage,
  '/market/competitors/:id': CompetitorDetailPage,
  '/market/competitions': TrackedCompetitionsPage,
  '/market/prizes': PrizeTrendsPage,
  '/market/pricing': PricingPage,
  '/market/activity': ActivityPage,
  '/competitions/listings': framed(ListingsPage),
  '/competitions/listings/new': framed(ListingFormPage),
  '/competitions/listings/:id': framed(ListingFormPage),
  '/competitions/publishing': framed(PublishingPage),
  '/competitions/templates': framed(TemplatesPage),
  '/marketing/campaigns': CampaignsListPage,
  '/marketing/campaigns/new': CampaignWizardPage,
  '/marketing/campaigns/:id': CampaignDetailPage,
  '/marketing/advertising': AdvertisingOverviewPage,
  '/marketing/attribution': AttributionPage,
  '/automation/workflows': framed(WorkflowsPage),
  '/automation/workflows/:id': framed(WorkflowDetailPage),
  '/automation/triggers': framed(TriggersPage),
  '/automation/scheduled': framed(AutomationScheduledPage),
  '/automation/activity': framed(AutomationActivityPage),
  '/reports': framed(ReportsListPage),
  '/reports/:id': framed(ReportDetailPage),
  '/reports/exports': framed(ExportsPage),
  '/integrations': framed(IntegrationsPage),
  '/integrations/:provider': framed(IntegrationProviderPage),
}

/** A section root opens the first tab the caller's role may use. */
function Alias({ section }: { section: SectionDef }) {
  const { search } = useLocation()
  const access = usePermissions()
  const tabs = useSectionTabs(section)
  if (access.isPending) return <LoadingBlock rows={3} />
  const to = tabs[0]?.to ?? section.tabs[0]?.path ?? '/'
  return <Navigate to={{ pathname: to, search }} replace />
}

export function AppRoutes() {
  return (
    <SignedOutGate>
      <Routes>
        <Route element={<AppShell />}>
          {ROUTES.map((route) => {
            const Page = route.kind === 'redirect' ? RedirectPage : (PAGES[route.path] ?? PlaceholderPage)
            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <RouteGate route={route}>
                    <Page route={route} />
                  </RouteGate>
                }
              />
            )
          })}
          {SECTION_ALIASES.map((alias) => {
            const section = SECTIONS.find((candidate) => candidate.key === alias.section)
            return section ? <Route key={alias.path} path={alias.path} element={<Alias section={section} />} /> : null
          })}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </SignedOutGate>
  )
}
